import React, { useEffect, useState } from 'react';
import { X, MapPin, CalendarClock, Users, CheckCircle, FileText } from 'lucide-react';
export interface ProposalOpportunity {
  id: string;
  title: string;
  zone: string;
  type: 'singola' | 'gruppo';
  jobsCount: number;
  window: string;
  distanceKm: number;
  suggestedUnitPrice: number;
}
interface ProposalWizardProps {
  isOpen: boolean;
  opportunity: ProposalOpportunity | null;
  onClose: () => void;
}
type ContractType = 'singolo' | 'c10' | 'c30';
const contractOptions: {
  id: ContractType;
  label: string;
  interventi: number;
  discountPct: number;
  note: string;
}[] = [{
  id: 'singolo',
  label: 'Intervento singolo',
  interventi: 1,
  discountPct: 0,
  note: 'Una tantum, nessun impegno ricorrente'
}, {
  id: 'c10',
  label: 'Contratto 10 interventi',
  interventi: 10,
  discountPct: 8,
  note: 'Prezzo unitario scontato, copertura zona'
}, {
  id: 'c30',
  label: 'Contratto 30 interventi',
  interventi: 30,
  discountPct: 15,
  note: 'Massimo risparmio, impegno ricorrente'
}];
export function ProposalWizard({
  isOpen,
  opportunity,
  onClose
}: ProposalWizardProps) {
  const [contract, setContract] = useState<ContractType>('c10');
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [submitted, setSubmitted] = useState(false);
  // Seed the unit price from the opportunity when it opens
  useEffect(() => {
    if (opportunity) {
      setUnitPrice(opportunity.suggestedUnitPrice);
      setContract('c10');
      setSubmitted(false);
    }
  }, [opportunity]);
  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);
  if (!isOpen || !opportunity) return null;
  const selected = contractOptions.find(o => o.id === contract)!;
  const effectiveUnit = Math.round(unitPrice * (1 - selected.discountPct / 100));
  const total = effectiveUnit * selected.interventi;
  return <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose} data-id="element-851">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" data-id="element-852">
        {/* Header */}
        <div className="flex items-center justify-between p-4 lg:p-6 border-b border-border" data-id="element-853">
          <div data-id="element-854">
            <h2 className="text-lg font-bold text-navy" data-id="element-855">Invia proposta</h2>
            <p className="text-xs text-muted-foreground mt-0.5" data-id="element-856">
              {opportunity.title}
            </p>
          </div>
          <button onClick={onClose} aria-label="Chiudi" className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-857">
            <X size={20} className="text-gray-500" data-id="element-858" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-5" data-id="element-859">
          {submitted ? <div className="text-center py-8" data-id="element-860">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-861">
                <CheckCircle size={32} className="text-green-600" data-id="element-862" />
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-863">
                Proposta inviata!
              </h3>
              <p className="text-sm text-muted-foreground max-w-[300px] mx-auto" data-id="element-864">
                La tua proposta da{' '}
                <span className="font-semibold text-foreground" data-id="element-865">
                  €{effectiveUnit}/intervento
                </span>{' '}
                ({selected.label.toLowerCase()}) è stata inviata. Riceverai una
                risposta a breve.
              </p>
            </div> : <>
              {/* Opportunity recap */}
              <div className="bg-gray-50 border border-border rounded-xl p-4 space-y-2" data-id="element-866">
                <div className="flex items-center gap-2 text-sm text-foreground" data-id="element-867">
                  <MapPin size={15} className="text-coral flex-shrink-0" data-id="element-868" />
                  <span className="font-medium" data-id="element-869">{opportunity.zone}</span>
                  <span className="text-muted-foreground" data-id="element-870">
                    · {opportunity.distanceKm} km
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-871">
                  <CalendarClock size={15} className="flex-shrink-0" data-id="element-872" />
                  {opportunity.window}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-873">
                  <Users size={15} className="flex-shrink-0" data-id="element-874" />
                  {opportunity.jobsCount}{' '}
                  {opportunity.jobsCount === 1 ? 'pulizia' : 'pulizie'}{' '}
                  {opportunity.type === 'gruppo' ? 'aggregate in zona' : 'vicino a un tuo job'}
                </div>
              </div>

              {/* Contract type */}
              <div data-id="element-875">
                <h3 className="text-sm font-semibold text-navy mb-2" data-id="element-876">
                  Tipo di accordo
                </h3>
                <div className="space-y-2" data-id="element-877">
                  {contractOptions.map(opt => {
                const active = contract === opt.id;
                return <button key={opt.id} type="button" onClick={() => setContract(opt.id)} className={`w-full text-left p-3 rounded-xl border-2 transition-all flex items-start justify-between gap-3 ${active ? 'border-coral bg-coral/5' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-878">
                        <div data-id="element-879">
                          <div className="flex items-center gap-2" data-id="element-880">
                            <span className="text-sm font-semibold text-navy" data-id="element-881">
                              {opt.label}
                            </span>
                            {opt.discountPct > 0 && <span className="text-[10px] font-medium px-2 py-0.5 bg-green-100 text-green-700 rounded-full" data-id="element-882">
                                -{opt.discountPct}%
                              </span>}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5" data-id="element-883">
                            {opt.note}
                          </p>
                        </div>
                        <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${active ? 'bg-coral border-coral' : 'border-gray-300'}`} data-id="element-884">
                          {active && <CheckCircle size={12} className="text-white" data-id="element-885" />}
                        </span>
                      </button>;
              })}
                </div>
              </div>

              {/* Unit price */}
              <div data-id="element-886">
                <h3 className="text-sm font-semibold text-navy mb-2" data-id="element-887">
                  Prezzo per intervento
                </h3>
                <div className="relative" data-id="element-888">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground" data-id="element-889">
                    €
                  </span>
                  <input type="number" min={0} value={unitPrice} onChange={e => setUnitPrice(Number(e.target.value))} className="w-full pl-7 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-890" />
                </div>
                <p className="text-xs text-muted-foreground mt-1" data-id="element-891">
                  Prezzo unitario valido per l'intero contratto/zona.
                </p>
              </div>

              {/* Summary */}
              <div className="bg-coral/5 border border-coral/20 rounded-xl p-4 space-y-2" data-id="element-892">
                <div className="flex justify-between text-sm" data-id="element-893">
                  <span className="text-muted-foreground" data-id="element-894">
                    Prezzo unitario effettivo
                  </span>
                  <span className="font-semibold text-foreground" data-id="element-895">
                    €{effectiveUnit}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-896">
                  <span className="text-muted-foreground" data-id="element-897">Interventi</span>
                  <span className="font-semibold text-foreground" data-id="element-898">
                    {selected.interventi}
                  </span>
                </div>
                <div className="flex justify-between text-base pt-2 border-t border-coral/20" data-id="element-899">
                  <span className="font-semibold text-navy" data-id="element-900">Valore totale</span>
                  <span className="font-bold text-coral" data-id="element-901">€{total}</span>
                </div>
              </div>
            </>}
        </div>

        {/* Footer */}
        {!submitted && <div className="p-4 lg:p-6 border-t border-border" data-id="element-902">
            <button onClick={() => setSubmitted(true)} disabled={!unitPrice} className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-colors min-h-[48px] ${unitPrice ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-903">
              <FileText size={18} data-id="element-904" />
              Invia proposta
            </button>
          </div>}
      </div>
    </div>;
}